import type { Block, BlockType } from '../schema/blocks'
import type { AnswerValue, StepAnswers } from '../schema/answers'
import { isAnswered, asStringArray } from '../schema/answers'

/**
 * ★ ตรรกะ "ช่องนี้ควรโผล่ไหม" มีที่เดียว
 *
 * ใช้ร่วมกันทั้งตอนเรนเดอร์ให้พนักงานเห็น, ตอนตรวจคำตอบ (ช่องที่ซ่อนอยู่ต้องไม่บังคับตอบ)
 * และตอนล้างคำตอบก่อนบันทึก — ถ้าแยกกันคิดเมื่อไหร่ จะเกิดอาการ "กดถัดไปไม่ได้เพราะช่องที่มองไม่เห็น"
 */

function compareText(value: AnswerValue | undefined, expected: string): boolean {
  if (value === undefined || value === null) return false
  if (Array.isArray(value)) return false
  if (typeof value === 'object') return false
  return String(value).trim() === expected.trim()
}

export function isBlockVisible(block: Block, answers: StepAnswers): boolean {
  const rule = block.visibleWhen
  if (!rule) return true

  const value = answers[rule.fieldName]
  switch (rule.kind) {
    case 'answered':
      return isAnswered(value)
    case 'notAnswered':
      return !isAnswered(value)
    case 'equals':
      return compareText(value, rule.value)
    case 'notEquals':
      return !compareText(value, rule.value)
    case 'includes':
      return asStringArray(value).some((item) => item.trim() === rule.value.trim())
  }
}

/**
 * บล็อกที่ต้องแสดงจริงตามคำตอบปัจจุบัน
 *
 * ประเมินไล่จากบนลงล่าง และใช้ผลลัพธ์สะสม — ช่องที่ถูกซ่อนจะถือว่า "ไม่มีคำตอบ"
 * สำหรับบล็อกที่อยู่ถัดลงไป เพื่อไม่ให้เกิดกรณีลูกโซ่ค้าง: ซ่อน A แล้ว B ที่อ้าง A ยังโผล่อยู่
 */
export function visibleBlocks(blocks: readonly Block[], answers: StepAnswers): Block[] {
  const effective: StepAnswers = { ...answers }
  const shown: Block[] = []
  for (const block of blocks) {
    if (!isBlockVisible(block, effective)) {
      if ('fieldName' in block) delete effective[block.fieldName]
      continue
    }
    shown.push(block)
  }
  return shown
}

/**
 * หาว่าช่องอำเภอ/ตำบล ควรอ่านค่าจังหวัด/อำเภอจากช่องไหน
 *
 * ★ ถ้าไม่ได้ระบุไว้ ให้หาบล็อกชนิดนั้นในหน้าเดียวกันเอง
 * ตอนลากบล็อกจังหวัด/อำเภอ/ตำบล มาวาง ค่านี้จะว่างไว้เสมอ ถ้าไม่เดาให้ ผลคือช่องอำเภอ
 * จะขึ้น "เลือกจังหวัดก่อน" ค้างตลอดกาลทั้งที่เลือกจังหวัดไปแล้ว — พังเงียบ ๆ โดยไม่มีอะไรบอก
 * (หนึ่งหน้าแทบไม่มีทางมีช่องจังหวัดเกินหนึ่งช่อง การเดาจึงตรงเกือบ 100%)
 */
export function resolveAddressSourceField(
  blocks: readonly Block[],
  explicitFieldName: string,
  sourceType: Extract<BlockType, 'provinceSelect' | 'districtSelect'>,
): string {
  if (explicitFieldName) {
    const exists = blocks.some(
      (block) => 'fieldName' in block && block.fieldName === explicitFieldName,
    )
    if (exists) return explicitFieldName
  }
  const found = blocks.find((block) => block.type === sourceType)
  return found && 'fieldName' in found ? found.fieldName : ''
}

/**
 * ตัดคำตอบของช่องที่ถูกซ่อนอยู่ทิ้งก่อนบันทึก
 * (เช่น พิมพ์ "อื่น ๆ" ไว้แล้วเปลี่ยนใจไปเลือกตัวเลือกอื่น — ข้อความเดิมต้องไม่ติดไปกับข้อมูลจริง)
 */
export function pruneHiddenAnswers(blocks: readonly Block[], answers: StepAnswers): StepAnswers {
  const shown = new Set(
    visibleBlocks(blocks, answers)
      .filter((block) => 'fieldName' in block)
      .map((block) => (block as { fieldName: string }).fieldName),
  )
  const hiddenFields = new Set(
    blocks
      .filter((block) => 'fieldName' in block)
      .map((block) => (block as { fieldName: string }).fieldName)
      .filter((fieldName) => !shown.has(fieldName)),
  )

  if (hiddenFields.size === 0) return answers
  const pruned: StepAnswers = {}
  for (const [fieldName, value] of Object.entries(answers)) {
    if (!hiddenFields.has(fieldName)) pruned[fieldName] = value
  }
  return pruned
}
